import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { motion } from 'framer-motion';
import {
  IoSwapHorizontal, IoBook, IoHeart, IoTrophy,
  IoPencil, IoNotifications, IoMoon, IoSunny, IoRocket, IoShield, IoCheckmarkCircle,
  IoArrowForward, IoFlame, IoReader, IoChatbubbles, IoSparkles,
  IoMail, IoStatsChart, IoColorPalette, IoMusicalNotes, IoBookmark,
  IoGlobe, IoLockClosed, IoTime, IoChevronDown
} from 'react-icons/io5';
import { useTheme } from '../context/ThemeContext';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './Landing.css';

/* ===== DATA ===== */
const FEATURES = [
  { icon: IoSwapHorizontal, title: 'Swipe & Découvre', desc: 'Comme Tinder, mais pour les livres. Swipe à droite pour ajouter, à gauche pour passer.', color: '#c8956c', tag: 'Découverte' },
  { icon: IoReader, title: 'Lecture immersive', desc: '6 thèmes visuels, lecture audio TTS, signets, estimation du temps et mode plein écran.', color: '#7a9e7e', tag: 'Lecture' },
  { icon: IoPencil, title: 'Espace auteur pro', desc: 'Éditeur riche, gestion de chapitres, brouillons, dashboard statistiques et export PDF.', color: '#8b6f8e', tag: 'Écriture' },
  { icon: IoChatbubbles, title: 'Social & communauté', desc: 'Messagerie privée temps réel, commentaires, réactions emoji, fil d\'activité et followers.', color: '#7a9ab5', tag: 'Social' },
  { icon: IoTrophy, title: 'Classements & concours', desc: 'Classements Hot, Top et Rising par genre. Concours littéraire hebdomadaire.', color: '#f59e0b', tag: 'Compétition' },
  { icon: IoHeart, title: 'Ta bibliothèque idéale', desc: 'Suivi de progression, listes thématiques, collections personnalisées et signets.', color: '#c27a7a', tag: 'Organisation' },
];

const AUTHOR_FEATURES = [
  { emoji: '✍️', title: 'Éditeur riche', desc: 'Gras, italique, titres, citations, séparateurs.' },
  { emoji: '📊', title: 'Dashboard stats', desc: 'Vues, likes, taux d\'engagement, graphiques.' },
  { emoji: '📄', title: 'Export PDF', desc: 'Exporte tes œuvres en PDF professionnel.' },
  { emoji: '📝', title: 'Brouillons', desc: 'Organise, prévisualise, publie quand tu veux.' },
  { emoji: '💬', title: 'Retours lecteurs', desc: 'Commentaires, notes étoilées, réactions.' },
  { emoji: '🏆', title: 'Concours', desc: 'Participe et gagne en visibilité.' },
  { emoji: '💰', title: 'Soutien auteur', desc: 'Reçois des tips de tes lecteurs.' },
  { emoji: '🚀', title: 'Boost', desc: 'Mets ton livre en avant avec des plumes.' },
];

const EXTRA_FEATURES = [
  { icon: IoSparkles, text: '10 badges à débloquer' },
  { icon: IoNotifications, text: 'Notifications temps réel' },
  { icon: IoMoon, text: 'Mode sombre complet' },
  { icon: IoRocket, text: 'Défis lecture hebdos' },
  { icon: IoShield, text: 'Modération & signalement' },
  { icon: IoCheckmarkCircle, text: 'Streaks quotidiennes' },
  { icon: IoFlame, text: 'Concours chaque semaine' },
  { icon: IoBook, text: '9 genres littéraires' },
  { icon: IoStatsChart, text: 'Niveaux lecteur' },
  { icon: IoColorPalette, text: '6 thèmes de lecture' },
  { icon: IoMusicalNotes, text: 'Lecture audio TTS' },
  { icon: IoBookmark, text: 'Signets & favoris' },
  { icon: IoGlobe, text: 'Résumés IA des livres' },
  { icon: IoLockClosed, text: 'Abonnement Premium' },
  { icon: IoTime, text: 'Temps de lecture estimé' },
];

const STATS = [
  { value: '95+', label: 'Fonctionnalités' },
  { value: '10', label: 'Badges' },
  { value: '6', label: 'Thèmes de lecture' },
  { value: '9', label: 'Genres littéraires' },
  { value: '∞', label: 'Histoires à découvrir' },
];

const STEPS = [
  { num: '01', title: 'Crée ton compte', desc: 'Gratuit, en 10 secondes. Email, Google ou Apple.', emoji: '✨', color: '#c8956c' },
  { num: '02', title: 'Swipe & découvre', desc: 'Parcours les livres comme sur Tinder. Droite = j\'adore.', emoji: '👆', color: '#7a9e7e' },
  { num: '03', title: 'Lis & participe', desc: 'Lis, commente, note, suis des auteurs. Bienvenue.', emoji: '📖', color: '#8b6f8e' },
];

const SHOWCASE = [
  { title: '📚 Bibliothèque', desc: 'Suivi de progression en temps réel', color: '#c8956c' },
  { title: '📖 Lecteur immersif', desc: '6 thèmes, audio TTS, signets', color: '#7a9e7e' },
  { title: '💬 Messagerie', desc: 'Discussions en temps réel', color: '#7a9ab5' },
  { title: '🏆 Classements', desc: 'Hot, Top, Rising par genre', color: '#f59e0b' },
];

const FAQ = [
  {
    q: "C'est quoi Plumly ?",
    a: "Plumly est une plateforme de lecture sociale gratuite. Tu peux découvrir des livres en swipant (comme Tinder !), lire gratuitement, publier tes propres histoires et rejoindre une communauté de passionnés de lecture."
  },
  {
    q: "Est-ce que Plumly est gratuit ?",
    a: "Oui ! La lecture, l'écriture et toutes les fonctionnalités principales sont gratuites. Un abonnement Premium optionnel existe pour supprimer les publicités, accéder à des stats avancées et soutenir le développement de la plateforme."
  },
  {
    q: "Comment publier un livre ?",
    a: "Crée un compte, va dans 'Mes œuvres', clique sur 'Nouveau', ajoute une couverture, un titre, un synopsis et un genre. Tu peux ensuite écrire tes chapitres avec notre éditeur riche et publier quand tu es prêt."
  },
  {
    q: "Comment fonctionne le système de plumes ?",
    a: "Les plumes sont la monnaie virtuelle de Plumly. Tu peux en acheter pour soutenir tes auteurs préférés, booster des livres dans les classements ou accéder à des fonctionnalités exclusives."
  },
  {
    q: "Puis-je gagner de l'argent en tant qu'auteur ?",
    a: "Oui ! Les lecteurs peuvent t'envoyer des plumes ou te soutenir financièrement. Via Stripe Connect, tu peux convertir tes gains en euros et recevoir un virement sur ton compte bancaire."
  },
  {
    q: "Quels genres de livres trouve-t-on sur Plumly ?",
    a: "Romance, Fantasy, Thriller, Sci-Fi, Drame, Horreur, Humour, Poésie, Aventure et bien plus. Chaque genre a ses propres classements et tu peux filtrer tes découvertes."
  },
];

const TESTIMONIALS = [
  { name: 'Emma L.', role: 'Lectrice', text: "Je passe mes soirées à swiper des livres. C'est addictif ! J'ai découvert des auteurs incroyables que je n'aurais jamais trouvés ailleurs.", avatar: '📚' },
  { name: 'Thomas R.', role: 'Auteur', text: "En 2 semaines, mon roman a eu plus de 200 vues. La communauté est bienveillante et les retours sont constructifs. Un vrai tremplin.", avatar: '✍️' },
  { name: 'Sarah M.', role: 'Lectrice & autrice', text: "L'interface est magnifique, la lecture est agréable et le système de plumes motive à écrire régulièrement. Mon app préférée.", avatar: '🌟' },
];

/* ===== HOOK IntersectionObserver ===== */
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, visible];
}

function RevealSection({ children, className = '', delay = 0 }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`landing-reveal ${visible ? 'landing-revealed' : ''} ${className}`}
      style={{ transitionDelay: delay + 'ms' }}
    >
      {children}
    </div>
  );
}

/* ===== COMPOSANT ===== */
export default function Landing() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleEmailSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!email || !email.includes('@') || !email.includes('.')) {
      setEmailStatus('error');
      setTimeout(() => setEmailStatus(null), 3000);
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'newsletter'), {
        email: email.toLowerCase().trim(),
        subscribedAt: new Date().toISOString(),
        source: 'landing'
      });

      setEmailStatus('success');
      setEmail('');
      setTimeout(() => setEmailStatus(null), 5000);
    } catch (err) {
      console.error('Erreur newsletter:', err);
      setEmailStatus('error');
    }
    setSubmitting(false);
  }, [email, submitting]);

  return (
    <div className="landing-page">
    <SEO />
      {/* Theme toggle */}
      <button className="landing-theme-toggle" onClick={toggleTheme} aria-label="Changer le thème">
        {theme === 'dark' ? <IoSunny /> : <IoMoon />}
      </button>

      {/* ===== HERO ===== */}
      <section className="landing-hero">
        <div className="landing-hero-bg">
          <div className="landing-hero-glow landing-hero-glow-1" />
          <div className="landing-hero-glow landing-hero-glow-2" />
        </div>

        <div className="landing-hero-content landing-hero-fade-in">
          <div className="landing-badge">🪶 Le Tinder des livres</div>
          <h1 className="landing-logo">Plumly</h1>
          <h2 className="landing-headline">
            Découvre, lis et partage<br />
            <span className="landing-headline-accent">en un swipe</span>
          </h2>
          <p className="landing-subheadline">
            La plateforme qui réinvente la lecture. Swipe pour découvrir des livres,
            lis gratuitement, publie tes écrits et rejoins une communauté de passionnés.
          </p>
          <div className="landing-cta-group">
            <motion.button
              className="landing-cta-btn"
              onClick={() => navigate('/auth')}
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              Commencer gratuitement <IoArrowForward />
            </motion.button>
            <button className="landing-cta-secondary" onClick={() => navigate('/auth')}>
              J'ai déjà un compte
            </button>
          </div>
          <p className="landing-trust">✓ 100% gratuit · ✓ RGPD · ✓ Aucun tracking</p>
        </div>

        <div className="landing-hero-visual landing-hero-fade-in landing-hero-fade-in-delay">
          <div className="landing-phone">
            <div className="landing-phone-notch" />
            <div className="landing-phone-screen">
              <div className="landing-phone-header">
                <span className="landing-phone-title">Swipe</span>
                <span className="landing-phone-sub">Trouve ta prochaine lecture</span>
              </div>
              <div className="landing-phone-card landing-float">
                <div className="landing-phone-cover" style={{ background: 'linear-gradient(135deg, #8b4557, #c4788a)' }}>
                  <span className="landing-phone-genre">💕 Romance</span>
                  <div className="landing-phone-book-info">
                    <strong>Les Étoiles de Minuit</strong>
                    <span>par Léa Martin</span>
                  </div>
                </div>
              </div>
              <div className="landing-phone-actions">
                <div className="landing-phone-btn-skip">✕</div>
                <div className="landing-phone-btn-like">♥</div>
              </div>
            </div>
          </div>
        </div>

        <div className="landing-scroll-indicator landing-bounce">
          <IoChevronDown />
        </div>
      </section>

      {/* ===== COMMENT ÇA MARCHE ===== */}
      <section className="landing-how">
        <RevealSection>
          <h2 className="landing-section-title">Comment ça marche ?</h2>
          <p className="landing-section-subtitle">Commence à lire en 3 étapes</p>
        </RevealSection>
        <div className="landing-how-grid">
          {STEPS.map((step, i) => (
            <RevealSection key={i} delay={i * 120}>
              <div className="landing-how-step">
                <div className="landing-how-num" style={{ color: step.color }}>{step.num}</div>
                <div className="landing-how-emoji">{step.emoji}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            </RevealSection>
          ))}
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="landing-features">
        <RevealSection>
          <h2 className="landing-section-title">Tout ce dont tu as besoin</h2>
          <p className="landing-section-subtitle">Une plateforme complète pour lire, écrire et partager</p>
        </RevealSection>
        <div className="landing-features-grid">
          {FEATURES.map((feat, i) => {
            const Icon = feat.icon;
            return (
              <RevealSection key={i} delay={i * 80}>
                <div className="landing-feature">
                  <div className="landing-feature-tag" style={{ color: feat.color }}>{feat.tag}</div>
                  <div className="landing-feature-icon" style={{ background: feat.color + '12', color: feat.color }}>
                    <Icon />
                  </div>
                  <h3>{feat.title}</h3>
                  <p>{feat.desc}</p>
                </div>
              </RevealSection>
            );
          })}
        </div>
      </section>

      {/* ===== SHOWCASE ===== */}
      <section className="landing-showcase">
        <RevealSection>
          <h2 className="landing-section-title">Une expérience pensée<br />pour les lecteurs</h2>
          <p className="landing-section-subtitle">Interface soignée, animations fluides, mode sombre</p>
        </RevealSection>
        <div className="landing-showcase-cards">
          {SHOWCASE.map((card, i) => (
            <RevealSection key={i} delay={i * 100}>
              <div className="landing-showcase-card">
                <div className="landing-showcase-card-accent" style={{ background: card.color }} />
                <h4>{card.title}</h4>
                <p>{card.desc}</p>
              </div>
            </RevealSection>
          ))}
        </div>
      </section>

      {/* ===== SECTION AUTEURS ===== */}
      <section className="landing-authors">
        <RevealSection>
          <h2 className="landing-section-title">Tu écris ? Plumly est fait pour toi.</h2>
          <p className="landing-section-subtitle">Tout ce qu'il faut pour publier et développer ton audience</p>
        </RevealSection>
        <div className="landing-authors-grid">
          {AUTHOR_FEATURES.map((feat, i) => (
            <RevealSection key={i} delay={i * 60}>
              <div className="landing-authors-item">
                <span>{feat.emoji}</span>
                <div>
                  <strong>{feat.title}</strong>
                  <p>{feat.desc}</p>
                </div>
              </div>
            </RevealSection>
          ))}
        </div>
      </section>

      {/* ===== FEATURES EXTRA ===== */}
      <section className="landing-extras">
        <RevealSection>
          <h2 className="landing-section-title">Et tellement plus...</h2>
          <p className="landing-section-subtitle">+95 fonctionnalités pour une expérience complète</p>
        </RevealSection>
        <div className="landing-extras-grid">
          {EXTRA_FEATURES.map((feat, i) => {
            const Icon = feat.icon;
            return (
              <RevealSection key={i} delay={i * 30}>
                <div className="landing-extra">
                  <Icon className="landing-extra-icon" />
                  <span>{feat.text}</span>
                </div>
              </RevealSection>
            );
          })}
        </div>
      </section>

      {/* ===== CHIFFRES ===== */}
      <section className="landing-stats">
        <div className="landing-stats-grid">
          {STATS.map((stat, i) => (
            <RevealSection key={i} delay={i * 100}>
              <div className="landing-stat-item">
                <span className="landing-stat-number">{stat.value}</span>
                <span className="landing-stat-label">{stat.label}</span>
              </div>
            </RevealSection>
          ))}
        </div>
      </section>

      {/* ===== NEWSLETTER ===== */}
      <section className="landing-newsletter">
        <RevealSection>
          <div className="landing-newsletter-content">
            <div className="landing-newsletter-icon"><IoMail /></div>
            <h2>Reste informé</h2>
            <p className="landing-newsletter-desc">Reçois les nouveautés, les meilleurs livres de la semaine et les annonces Plumly.</p>
            <form className="landing-newsletter-form" onSubmit={handleEmailSubmit}>
              <div className="landing-newsletter-input-wrap">
                <IoMail className="landing-newsletter-input-icon" />
                <input
                  type="email"
                  placeholder="ton@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="landing-newsletter-input"
                />
              </div>
              <motion.button
                type="submit"
                className="landing-newsletter-btn"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                disabled={submitting}
              >
                {submitting ? '...' : "S'inscrire"}
              </motion.button>
            </form>
            {emailStatus === 'success' && (
              <p className="landing-newsletter-success landing-fade-in-fast">
                ✓ Inscription réussie ! On te préviendra au lancement 🪶
              </p>
            )}
            {emailStatus === 'already' && (
              <p className="landing-newsletter-already landing-fade-in-fast">
                Tu es déjà inscrit(e) ! On ne t'a pas oublié 😉
              </p>
            )}
            {emailStatus === 'error' && (
              <p className="landing-newsletter-error landing-fade-in-fast">
                Merci d'entrer une adresse email valide.
              </p>
            )}
            <p className="landing-newsletter-disclaimer">Pas de spam, promis. Désinscription en un clic.</p>
          </div>
        </RevealSection>
      </section>

      {/* ===== TÉMOIGNAGES ===== */}
      <RevealSection>
        <section className="landing-section">
          <h2 className="landing-section-title">Ce qu'ils en pensent</h2>
          <p className="landing-section-subtitle">Des lecteurs et auteurs qui ont adopté Plumly</p>
          <div className="landing-testimonials">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="landing-testimonial">
                <div className="landing-testimonial-avatar">{t.avatar}</div>
                <p className="landing-testimonial-text">"{t.text}"</p>
                <div className="landing-testimonial-author">
                  <strong>{t.name}</strong>
                  <span>{t.role}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </RevealSection>

      {/* ===== FAQ ===== */}
      <RevealSection>
        <section className="landing-section landing-faq-section">
          <h2 className="landing-section-title">Questions fréquentes</h2>
          <div className="landing-faq">
            {FAQ.map((item, i) => (
              <details key={i} className="landing-faq-item">
                <summary className="landing-faq-question">{item.q}</summary>
                <p className="landing-faq-answer">{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      </RevealSection>

      {/* ===== CTA FINAL ===== */}
      <section className="landing-final">
        <RevealSection>
          <div className="landing-final-content">
            <h2>Prêt à trouver ta prochaine<br />lecture préférée ?</h2>
            <p>Rejoins Plumly gratuitement et commence à swiper.</p>
            <motion.button
              className="landing-cta-btn landing-cta-btn-light"
              onClick={() => navigate('/auth')}
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              Créer mon compte gratuit <IoArrowForward />
            </motion.button>
          </div>
        </RevealSection>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="landing-footer">
        <div className="landing-footer-brand">🪶 Plumly</div>
        <p>Le Tinder des livres — Lis, écris, partage.</p>
        <div className="landing-footer-links">
          <span onClick={() => navigate('/about')}>À propos</span>
          <span>·</span>
          <span onClick={() => navigate('/legal/cgu')}>CGU</span>
          <span>·</span>
          <span onClick={() => navigate('/legal/privacy')}>Confidentialité</span>
        </div>
        <p className="landing-footer-copy">© 2025 Plumly. Fait avec passion 🪶</p>
      </footer>
    </div>
  );
}